// Predicado ÚNICO (10.1) de "cuota de plan_cobros_facturas/plan_pagos_facturas reagendable" — Fase
// 2b de "Futuros: reagendar" (2026-09-24, cierre de fisuras de la auditoría independiente de Fase 2);
// ENDURECIDO en Fase 2d (2026-09-24, cierre de fisuras de la auditoría de 2b+2c, fisura a): el
// criterio del libro pasa de "Σ aplicado ≤ TOLERANCIA_MISMA_MONEDA" a EXISTS puro — el MISMO criterio,
// sin tolerancia, que ya usaba (y sigue usando) la RPC `reagendar_cuota_factura` (candado real de
// fila; ver su migración, paso 5: `EXISTS (SELECT 1 FROM aplicaciones_saldo WHERE cuota_id = ...)`).
// Antes de este cierre, la PANTALLA (este archivo, con la suma+tolerancia) y la RPC (con EXISTS)
// podían divergir en el borde exacto de la tolerancia — un evento de $1 (dentro de
// TOLERANCIA_MISMA_MONEDA) dejaba la pantalla en "reagendable" mientras la RPC ya veía una fila real
// en el libro y rechazaba. Cerrado: ambos lados usan ahora EXISTS puro, sin ningún concepto de monto.
//
// ANTES de la Fase 2b, el LOADER (`getCuotasCobrosFuturas`/`getCuotasPagosFuturas`, que decide qué se
// MUESTRA como pendiente), la PANTALLA (`esFechaReagendable`/`reagendarEntryFuturo.js`, que decide qué
// es CLICKEABLE) y el WRITER (`guardarFechaCuotaFactura`, que decide qué se ESCRIBE) usaban criterios
// DISTINTOS de "cuota pendiente" — podían divergir. Caso real que lo probó: la cuota 52 del documento
// 8735 tiene un pago PARCIAL en el libro `aplicaciones_saldo` (1.000.000 aplicado de un total de
// 1.415.326) — con EXISTS, cualquier fila (sea $1.000.000 o $1) bloquea por igual, así que ese caso
// real queda cubierto sin necesidad de sumar montos. Este archivo es ahora la ÚNICA fuente del
// criterio, consumida por el LOADER (`planCobros.js`, que calcula `_tiene_eventos` del MISMO array de
// eventos que ya trae del libro — sin fetch adicional), la PANTALLA (`reagendarEntryFuturo.js`) y —
// mismo criterio EXISTS, aplicado de forma independiente dentro de su propia transacción — la RPC
// `reagendar_cuota_factura` (ver `supabase/migrations/20260924_rpc_reagendar_cuota_factura.sql`).
// `guardarFechaFactura.js` (el writer de la factura ENTERA) reusa el MISMO criterio EXISTS para decidir
// si borra el plan de cuotas al pasar a pago único — nunca un tercer criterio propio (10.1).

/**
 * `true` SOLO SI TODAS estas condiciones se cumplen — lista BLANCA (nunca una lista negra de estados
 * "no reagendables"): cualquier valor de `estado` que no sea EXACTAMENTE `"pendiente"` bloquea,
 * incluido cualquier estado futuro/desconocido que este código todavía no conozca.
 *
 *   1. `estado === "pendiente"` — nunca "pagado"/"cobrado" ni ningún otro string.
 *
 *   2. `movimiento_bancario_id == null` — sin vínculo a un movimiento bancario real.
 *
 *   3. `!tieneEventos` — EXISTS puro: CUALQUIER fila en `aplicaciones_saldo` para esta `cuota_id`
 *      bloquea, sin importar el monto (Fase 2d — ya NO hay tolerancia acá). MISMO criterio que usa
 *      `reagendar_cuota_factura` en la RPC (EXISTS, no Σ). `tieneEventos` lo calcula el CALLER (el
 *      LOADER, `planCobros.js`) a partir del MISMO array de eventos del libro que ya trae para calcular
 *      la disponibilidad — este predicado sigue siendo puro (no consulta la DB, no importa ninguna
 *      constante de tolerancia).
 *
 *   4. `documento_tipo !== "factura_externa"` — exclusión CATEGÓRICA, independiente de `estado`.
 *      Motivo (fisura real, verificada contra `supabase/migrations/20260920_rpc_pago_externas.sql`,
 *      sección 2b de `_vincular_bancario_sii`, comentario "externas no tienen cuotas"): cuando una
 *      externa se paga por esa RPC, el paso 2b EXCLUYE explícitamente `v_tipo_fila = 'factura_externa'`
 *      del UPDATE que marca `plan_pagos_facturas.estado='pagado'` — cierto para el flujo hoy vivo del
 *      modal (`handleConfirmarVincularExternas`), que paga la externa COMPLETA sin seleccionar
 *      `cuota_ids` (grep confirmado: nunca arma ese payload para externas). El evento que sí se
 *      escribe en el libro (paso 4 de la misma RPC) puede entonces quedar con `cuota_id = NULL`
 *      (aplica al documento completo, no a una cuota puntual) — el chequeo (3) de arriba, que filtra
 *      `aplicaciones_saldo` por `cuota_id`, no lo captura necesariamente. Resultado: una cuota de
 *      externa ya pagada por esa vía puede seguir viendo `estado='pendiente'` Y sin un evento propio
 *      en el libro por `cuota_id` — sin esta exclusión categórica, quedaría reagendable por error.
 *      Documentos SII (emitida/recibida) y boletas de honorarios SÍ marcan `estado` correctamente (2b
 *      corre para ellos) y SÍ registran `cuota_id` cuando corresponde, así que no necesitan esta
 *      exclusión — solo `factura_externa` la necesita.
 *
 * @param {Object} params
 * @param {string} [params.estado] - `plan_cobros_facturas.estado` / `plan_pagos_facturas.estado`.
 * @param {number|null} [params.movimiento_bancario_id]
 * @param {boolean} [params.tieneEventos] - `true` si existe AL MENOS una fila en
 *   `aplicaciones_saldo` para esta `cuota_id`, sin importar el monto (EXISTS, no Σ). El caller la
 *   calcula — este predicado no consulta la DB (puro). `undefined`/`false` se tratan igual (falsy):
 *   sin eventos conocidos, no bloquea.
 * @param {string} [params.documento_tipo] - documento_tipo REAL de la cuota: sii_emitida | sii_recibida
 *   | factura_externa | boleta_honorario.
 * @returns {boolean}
 */
export const esCuotaReagendable = ({ estado, movimiento_bancario_id, tieneEventos, documento_tipo } = {}) => {
  if (estado !== "pendiente") return false;
  if (movimiento_bancario_id != null) return false;
  if (tieneEventos) return false;
  if (documento_tipo === "factura_externa") return false;
  return true;
};
